import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { Edit3, Plus, RefreshCw, UserCheck, UserX, X } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import StatusPill from "../../components/common/StatusPill";
import StaffDrawer from "./components/StaffDrawer";
import TeamAccessLayout, {
  accessButton,
  accessInput,
} from "../Settings/AccessControl/TeamAccessLayout";
import {
  createTeamStaff,
  getTeamStaffList,
  getTeamStaffRoles,
  setTeamStaffStatus,
  updateTeamStaff,
} from "../../api/teamStaffApi";

function extractArrayPayload(res) {
  const data = res?.data?.data ?? res?.data?.Data ?? res?.data ?? res;
  return Array.isArray(data) ? data : [];
}

function normalizeRole(r) {
  return {
    id: r?.id ?? r?.Id,
    name: r?.name ?? r?.Name ?? "",
    isActive: r?.isActive ?? r?.IsActive ?? true,
  };
}

function normalizeUser(u) {
  return {
    id: u?.id ?? u?.Id,
    name: u?.name ?? u?.Name ?? "",
    email: u?.email ?? u?.Email ?? "",
    roleId: u?.roleId ?? u?.RoleId,
    roleName: u?.roleName ?? u?.RoleName ?? "",
    status: u?.status ?? u?.Status ?? "",
    createdAt: u?.createdAt ?? u?.CreatedAt,
    updatedAt:
      u?.updatedAt ?? u?.UpdatedAt ?? u?.lastUpdatedAt ?? u?.LastUpdatedAt,
    hasWhatsAppPhone: u?.hasWhatsAppPhone ?? u?.HasWhatsAppPhone ?? false,
    whatsAppPhoneNormalized:
      u?.whatsAppPhoneNormalized ?? u?.WhatsAppPhoneNormalized ?? "",
    whatsAppPhoneMasked: u?.whatsAppPhoneMasked ?? u?.WhatsAppPhoneMasked ?? "",
    isWhatsAppPhoneVerified:
      u?.isWhatsAppPhoneVerified ?? u?.IsWhatsAppPhoneVerified ?? false,
  };
}

export default function TeamStaffPage() {
  const { can, hasAllAccess, user } = useAuth();
  const allowed = (code) => hasAllAccess || can?.(code);
  const canView =
    allowed(FK.SETTINGS_STAFF_MANAGEMENT) || allowed("teamstaff.view");
  const canManage =
    allowed(FK.SETTINGS_STAFF_MANAGEMENT) || allowed("teamstaff.manage");
  const [users, setUsers] = useState([]),
    [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [rolesError, setRolesError] = useState(false);
  const [query, setQuery] = useState(""),
    [roleFilter, setRoleFilter] = useState("all"),
    [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null),
    [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(null);
  const request = useRef(0),
    mutation = useRef(false);
  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setError("");
    setRolesError(false);
    const [staffResult, roleResult] = await Promise.allSettled([
      getTeamStaffList(),
      getTeamStaffRoles(),
    ]);
    if (id !== request.current) return;
    if (staffResult.status === "fulfilled")
      setUsers(
        extractArrayPayload(staffResult.value)
          .map(normalizeUser)
          .filter((member) => member.id),
      );
    else setError("Team members could not be loaded.");
    if (roleResult.status === "fulfilled")
      setRoles(
        extractArrayPayload(roleResult.value)
          .map(normalizeRole)
          .filter((role) => role.id && role.name && role.isActive),
      );
    else {
      setRoles([]);
      setRolesError(true);
    }
    setLoading(false);
  }, []);
  useEffect(() => {
    if (canView) load();
    return () => {
      request.current += 1;
    };
  }, [canView, load]);
  const filtered = useMemo(
    () =>
      users.filter(
        (member) =>
          (roleFilter === "all" || String(member.roleId) === roleFilter) &&
          (statusFilter === "all" ||
            member.status.toLowerCase() === statusFilter) &&
          [member.name, member.email, member.roleName]
            .join(" ")
            .toLowerCase()
            .includes(query.trim().toLowerCase()),
      ),
    [users, query, roleFilter, statusFilter],
  );
  const protectedMember = (member) =>
    String(member.id) === String(user?.id || user?.userId || "") ||
    [
      "business",
      "business.owner",
      "owner",
      "businessowner",
      "businessadmin",
    ].includes(member.roleName.toLowerCase());
  const changeStatus = async (member) => {
    if (!canManage || protectedMember(member) || mutation.current) return;
    const next = member.status.toLowerCase() === "active" ? "Hold" : "Active";
    if (
      !window.confirm(
        `${next === "Hold" ? "Put on hold" : "Activate"}: ${member.name}?`,
      )
    )
      return;
    mutation.current = true;
    setBusy(member.id);
    try {
      await setTeamStaffStatus(member.id, next);
      toast.success("Team member status updated.");
      setDetail(null);
      await load();
    } catch (failure) {
      toast.error(
        failure?.response?.data?.message || "Status update failed. Try again.",
      );
    } finally {
      mutation.current = false;
      setBusy(null);
    }
  };
  const saveMember = async (id, payload) => {
    if (!canManage)
      throw new Error("You do not have permission to manage team members.");
    if (id) await updateTeamStaff(id, payload);
    else await createTeamStaff(payload);
    toast.success(id ? "Team member updated." : "Team member created.");
    setEditor(null);
    setDetail(null);
    await load();
  };
  if (!canView)
    return (
      <TeamAccessLayout>
        <p role="alert">You do not have access to team members.</p>
      </TeamAccessLayout>
    );
  return (
    <TeamAccessLayout>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Team members</h2>
          <p className="mt-1 text-xs text-slate-500">
            {users.length} members /{" "}
            {
              users.filter((member) => member.status.toLowerCase() === "active")
                .length
            }{" "}
            active /{" "}
            {
              users.filter((member) => member.status.toLowerCase() === "hold")
                .length
            }{" "}
            on hold
          </p>
        </div>
        <div className="flex gap-2">
          <button
            aria-label="Refresh team members"
            title="Refresh team members"
            className={`${accessButton} w-9 !px-0`}
            disabled={loading || Boolean(busy)}
            onClick={load}
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          {canManage && (
            <button
              className={`${accessButton} !border-emerald-600 !text-emerald-700`}
              disabled={loading || rolesError || !roles.length}
              onClick={() => setEditor({ mode: "create" })}
            >
              <Plus size={16} />
              Add team member
            </button>
          )}
        </div>
      </div>
      {rolesError ? (
        <p
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3"
        >
          Roles could not be loaded.{" "}
          <button className="underline" onClick={load}>
            Retry loading roles
          </button>
        </p>
      ) : (
        !loading &&
        !roles.length && (
          <p className="rounded-lg border border-slate-200 bg-white p-3 text-slate-600">
            No assignable roles yet. Create an active role in Roles &
            permissions before adding team members.
          </p>
        )
      )}
      <section
        aria-label="Team member directory"
        className="overflow-hidden rounded-lg border border-slate-200 bg-white"
      >
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 p-4">
          <input
            aria-label="Search team members"
            placeholder="Search name or email"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className={`${accessInput} w-64`}
          />
          <select
            aria-label="Filter by role"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            className={accessInput}
          >
            <option value="all">All roles</option>
            {[
              ...new Map(
                users
                  .filter((member) => member.roleId)
                  .map((member) => [String(member.roleId), member.roleName]),
              ).entries(),
            ].map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className={accessInput}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="hold">On hold</option>
          </select>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-100 text-xs text-slate-500">
            <tr>
              {["Name / email", "Role", "WhatsApp", "Status", "Actions"].map(
                (label) => (
                  <th className="px-4 py-3 font-medium" key={label} scope="col">
                    {label}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  role="status"
                  className="p-8 text-center text-slate-500"
                >
                  Loading team members...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} role="alert" className="p-8 text-center">
                  {error}{" "}
                  <button className="underline" onClick={load}>
                    Retry
                  </button>
                </td>
              </tr>
            ) : !filtered.length ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-slate-500">
                  {users.length
                    ? "No team members match these filters."
                    : "No team members yet."}
                </td>
              </tr>
            ) : (
              filtered.map((member) => (
                <tr key={member.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <button
                      className="text-emerald-700 hover:underline"
                      onClick={() => setDetail(member)}
                    >
                      {member.name || "Team member"}
                    </button>
                    <p className="mt-1 text-xs text-slate-500">
                      {member.email}
                    </p>
                  </td>
                  <td className="px-4 py-3">{member.roleName || "No role"}</td>
                  <td className="px-4 py-3">
                    {member.hasWhatsAppPhone
                      ? member.whatsAppPhoneMasked || "Configured"
                      : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={member.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {canManage && !protectedMember(member) ? (
                        <>
                          <button
                            className={accessButton}
                            disabled={rolesError || loading || Boolean(busy)}
                            onClick={() =>
                              setEditor({
                                mode: "edit",
                                initial: {
                                  ...member,
                                  whatsAppPhone: member.whatsAppPhoneNormalized,
                                },
                              })
                            }
                          >
                            <Edit3 size={14} />
                            Edit
                          </button>
                          <button
                            className={accessButton}
                            disabled={Boolean(busy)}
                            onClick={() => changeStatus(member)}
                          >
                            {member.status.toLowerCase() === "active" ? (
                              <UserX size={14} />
                            ) : (
                              <UserCheck size={14} />
                            )}
                            {member.status.toLowerCase() === "active"
                              ? "Hold"
                              : "Activate"}
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-slate-500">
                          {protectedMember(member)
                            ? "Protected account"
                            : "View only"}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <footer className="border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
          {!loading &&
            !error &&
            `${filtered.length} of ${users.length} members`}
        </footer>
      </section>
      <Dialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        className="fixed inset-0 z-50"
      >
        <div className="fixed inset-0 bg-slate-950/30" aria-hidden="true" />
        <DialogPanel className="absolute inset-y-0 right-0 w-[460px] max-w-full overflow-y-auto bg-white p-6 shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-200 pb-4">
            <DialogTitle className="text-base font-semibold">
              Member details
            </DialogTitle>
            <button
              aria-label="Close member details"
              className={`${accessButton} w-9 !px-0`}
              onClick={() => setDetail(null)}
            >
              <X size={16} />
            </button>
          </div>
          {detail && (
            <>
              <h3 className="mt-5 text-lg font-semibold">{detail.name}</h3>
              <p className="mt-1 text-slate-500">{detail.email}</p>
              <dl className="mt-6 grid grid-cols-2 gap-5">
                {[
                  ["Role", detail.roleName],
                  ["Status", detail.status],
                  ["WhatsApp", detail.whatsAppPhoneMasked],
                  ["Created", detail.createdAt?.slice(0, 10)],
                  ["Last updated", detail.updatedAt?.slice(0, 10)],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-slate-500">{label}</dt>
                    <dd className="mt-1">{value || "-"}</dd>
                  </div>
                ))}
              </dl>
              {canManage && !protectedMember(detail) && (
                <button
                  className={`${accessButton} mt-6`}
                  disabled={rolesError}
                  onClick={() => {
                    setEditor({
                      mode: "edit",
                      initial: {
                        ...detail,
                        whatsAppPhone: detail.whatsAppPhoneNormalized,
                      },
                    });
                    setDetail(null);
                  }}
                >
                  <Edit3 size={16} />
                  Edit member
                </button>
              )}
            </>
          )}
        </DialogPanel>
      </Dialog>
      <StaffDrawer
        open={Boolean(editor)}
        mode={editor?.mode}
        initial={editor?.initial}
        roles={roles}
        onClose={() => setEditor(null)}
        onCreate={(payload) => saveMember(null, payload)}
        onUpdate={saveMember}
      />
    </TeamAccessLayout>
  );
}
